import type { QueryClient } from '@tanstack/react-query';
import { api, type ProjectResourceSummary } from '@/api/client';
import { PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY, ARCHIVED_PROJECT_IDS_QUERY_KEY } from '@/lib/projectEntityCache';
import { readyMonthProjectIdsPredicate } from '@/lib/projectListQueryCache';

/** Сигнал для таблицы: полный батч сводок по всем видимым id (явный сброс). */
export const SUMMARIES_BATCH_RELOAD_QUERY_KEY = ['summariesBatchReloadSignal'] as const;

/** Сигнал для таблицы: перезапрос только «дырявых» id после точечной инвалидации. */
export const SUMMARIES_DELTA_BUMP_QUERY_KEY = ['summariesDeltaBumpSignal'] as const;

type SummariesDeltaSignal = {
  at: number;
  projectIds: string[];
};

const EMPTY_RESOURCE: ProjectResourceSummary = {
  noteCount: 0,
  recentNoteCount: 0,
  fileCount: 0,
  recentFileCount: 0,
  recordedSessionCount: 0,
  plannedSessionCount: 0,
  plannerStatus: null,
};

function resourceSummaryEqual(a: ProjectResourceSummary, b: ProjectResourceSummary): boolean {
  return (
    a.noteCount === b.noteCount &&
    a.recentNoteCount === b.recentNoteCount &&
    a.fileCount === b.fileCount &&
    a.recentFileCount === b.recentFileCount &&
    a.recordedSessionCount === b.recordedSessionCount &&
    a.plannedSessionCount === b.plannedSessionCount &&
    a.plannerStatus === b.plannerStatus
  );
}

export function bumpSummariesBatchReload(queryClient: QueryClient): void {
  queryClient.setQueryData(SUMMARIES_BATCH_RELOAD_QUERY_KEY, Date.now());
}

export function bumpSummariesDelta(queryClient: QueryClient): void {
  queryClient.setQueryData<SummariesDeltaSignal>(SUMMARIES_DELTA_BUMP_QUERY_KEY, (prev) => ({
    at: Date.now(),
    projectIds: prev?.projectIds ?? [],
  }));
}

export function getDirtySummaryProjectIds(queryClient: QueryClient): string[] {
  return queryClient.getQueryData<SummariesDeltaSignal>(SUMMARIES_DELTA_BUMP_QUERY_KEY)?.projectIds ?? [];
}

export function consumeDirtySummaryProjectIds(queryClient: QueryClient, projectIds: string[]): void {
  const unique = new Set(projectIds.filter(Boolean));
  if (unique.size === 0) return;

  queryClient.setQueryData<SummariesDeltaSignal>(SUMMARIES_DELTA_BUMP_QUERY_KEY, (prev) => {
    if (!prev) {
      return {
        at: Date.now(),
        projectIds: [],
      };
    }

    const nextProjectIds = prev.projectIds.filter((id) => !unique.has(id));
    if (nextProjectIds.length === prev.projectIds.length) {
      return prev;
    }

    return {
      ...prev,
      projectIds: nextProjectIds,
    };
  });
}

/** Нормализованные кэши `['cellCommentSummaries', id]` / `['projectResourceSummaries', id]`. */
export function clearPerProjectSummariesCaches(queryClient: QueryClient): void {
  queryClient.removeQueries({
    predicate: (q) => {
      const k = q.queryKey as unknown[];
      return (
        Array.isArray(k) &&
        k.length === 2 &&
        typeof k[1] === 'string' &&
        (k[0] === 'cellCommentSummaries' || k[0] === 'projectResourceSummaries')
      );
    },
  });
}

/** Все id проектов в активной таблице и в загруженных кэшах «готовых по месяцам». */
export function collectTableProjectIds(queryClient: QueryClient): string[] {
  const ids = new Set<string>();
  const activeIds = queryClient.getQueryData<string[]>(PROJECTS_TABLE_ACTIVE_IDS_QUERY_KEY);
  activeIds?.forEach((id) => ids.add(id));
  const ready = queryClient.getQueriesData<string[]>({ predicate: readyMonthProjectIdsPredicate });
  for (const [, list] of ready) {
    list?.forEach((id) => ids.add(id));
  }
  const archivedIds = queryClient.getQueryData<string[]>(ARCHIVED_PROJECT_IDS_QUERY_KEY);
  archivedIds?.forEach((id) => ids.add(id));
  return [...ids];
}

/**
 * Батч сводок с сервера и `setQueryData` только для id, где данные отличаются от кэша
 * (без clear, без bump полного reload — меньше лишних перерисовок).
 */
export async function reloadTableRowSummariesDiff(queryClient: QueryClient): Promise<void> {
  const ids = collectTableProjectIds(queryClient);
  if (ids.length === 0) return;

  const [cell, res] = await Promise.all([
    api.projects.cellCommentSummaries(ids),
    api.projects.projectResourceSummaries(ids),
  ]);

  for (const id of ids) {
    const cellRow = cell.summaries[id] ?? {};
    const prev = queryClient.getQueryData<Record<string, number>>(['cellCommentSummaries', id]);
    const same = prev && JSON.stringify(prev) === JSON.stringify(cellRow);
    if (!same) {
      queryClient.setQueryData(['cellCommentSummaries', id], cellRow);
    }

    const resRow = res.summaries[id] ?? EMPTY_RESOURCE;
    const prevR = queryClient.getQueryData<ProjectResourceSummary>(['projectResourceSummaries', id]);
    if (!prevR || !resourceSummaryEqual(prevR, resRow)) {
      queryClient.setQueryData(['projectResourceSummaries', id], resRow);
    }
  }
}

/**
 * После переподключения WS / reconcile: тот же батч, что и `reloadTableRowSummariesDiff`.
 * Раньше очищал все per-id кэши и поднимал полный reload-сигнал.
 */
export async function reloadTableRowSummariesFromServer(queryClient: QueryClient): Promise<void> {
  await reloadTableRowSummariesDiff(queryClient);
}

/** Сброс сводок только для указанных проектов → дельта-батч по ним (без полного refetch таблицы). */
export function invalidateSummariesForProjectIds(queryClient: QueryClient, projectIds: string[]): void {
  const unique = [...new Set(projectIds.filter(Boolean))];
  if (unique.length === 0) return;

  queryClient.setQueryData<SummariesDeltaSignal>(SUMMARIES_DELTA_BUMP_QUERY_KEY, (prev) => ({
    at: Date.now(),
    projectIds: [...new Set([...(prev?.projectIds ?? []), ...unique])],
  }));
}
